import { useState, useMemo, useCallback } from "react";
import { useAuthentication, useDialog, useFavorites } from "hooks";
import { useNavigate } from "react-router-dom";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Typography,
  Alert,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  Skeleton,
} from "@mui/material";
import {
  BasePage,
  NotificationTypography,
  DealCard,
  LikeChip,
  ShareButton,
  ChipButton,
  OpenHoursTypography,
  RatingChip,
  DealGridSkeleton,
  ReviewListItem,
  ReviewListSkeleton,
} from "components";
import BlockIcon from "@mui/icons-material/Block";
import CallIcon from "@mui/icons-material/Call";
import LoginIcon from "@mui/icons-material/Login";
import PublicIcon from "@mui/icons-material/Public";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { API_ENDPOINTS } from "constants";

export function FavoritesPage() {
  const { user, isAuthenticated } = useAuthentication();
  const [tab, setTab] = useState("deals");

  const {
    deals,
    restaurants,
    likedReviews,
    loading,
    error,
    likeDeal,
    unlikeDeal,
    likeRestaurant,
    unlikeRestaurant,
    likeReview,
    unlikeReview,
  } = useFavorites();

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
  };

  if (!isAuthenticated) {
    return (
      <BasePage>
        <NotificationTypography
          color="info"
          Icon={LoginIcon}
          title="Sign In Required"
          message="Please sign in to view your favorites."
          showActions={false}
        />
      </BasePage>
    );
  }

  if (error) {
    return (
      <BasePage>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading favorites: {error}
        </Alert>
      </BasePage>
    );
  }

  return (
    <BasePage>
      <Typography variant="h6" mb={3}>
        Favourites
      </Typography>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange}>
            <Tab label={`Deals (${deals.length})`} value="deals" />
            <Tab label={`Restaurants (${restaurants.length})`} value="restaurants" />
            <Tab label={`Reviews (${likedReviews.length})`} value="reviews" />
          </TabList>
        </Box>
        <TabPanel value="deals" sx={{ px: 0 }}>
          <FavoriteDealsSection
            deals={deals}
            loading={loading}
            likeDeal={likeDeal}
            unlikeDeal={unlikeDeal}
          />
        </TabPanel>
        <TabPanel value="restaurants" sx={{ px: 0 }}>
          <FavoriteRestaurantsSection
            restaurants={restaurants}
            loading={loading}
            likeRestaurant={likeRestaurant}
            unlikeRestaurant={unlikeRestaurant}
          />
        </TabPanel>
        <TabPanel value="reviews" sx={{ px: 0 }}>
          <LikedReviewsSection
            reviews={likedReviews}
            loading={loading}
            error={error}
            user={user}
            likeReview={likeReview}
            unlikeReview={unlikeReview}
          />
        </TabPanel>
      </TabContext>
    </BasePage>
  );
}

function FavoriteDealsSection({ deals, loading, likeDeal, unlikeDeal }) {
  const { user } = useAuthentication();

  const renderedDeals = useMemo(() => {
    return deals.map((deal) => {
      const isLiked = !!(user && deal.likedBy?.includes(user.uid));
      const isOwner = user?.uid === deal.userId;

      return (
        <DealCard
          key={deal.id}
          deal={deal}
          onLike={likeDeal}
          onUnlike={unlikeDeal}
          isLiked={isLiked}
          currentUser={user}
          isOwner={isOwner}
          showTimeTypography={false}
        />
      );
    });
  }, [deals, user, likeDeal, unlikeDeal]);

  if (loading) {
    return <DealGridSkeleton showWeekButtons={false} />;
  }

  if (deals.length === 0) {
    return (
      <NotificationTypography
        color="info"
        Icon={SearchOffIcon}
        title="No Favorite Deals"
        message="You haven't favorited any deals yet."
        showActions={false}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        },
        gap: 2,
        width: "100%",
        alignItems: "start",
      }}
    >
      {renderedDeals}
    </Box>
  );
}

function LikedReviewsSection({ reviews, loading, error, user, likeReview, unlikeReview }) {
  if (loading) {
    return <ReviewListSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading liked reviews: {error}
      </Alert>
    );
  }

  if (reviews.length === 0) {
    return (
      <NotificationTypography
        color="info"
        Icon={SearchOffIcon}
        title="No Liked Reviews"
        message="You haven't liked any reviews yet."
        showActions={false}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      {reviews.map((review) => (
        <ReviewListItem
          key={review.id}
          review={review}
          user={user}
          editingId={null}
          editData={{}}
          onStartEdit={() => {}}
          onCancelEdit={() => {}}
          onEditChange={() => {}}
          onEditSubmit={() => {}}
          onDelete={() => {}}
          onDeleteDraft={() => {}}
          onLike={(reviewId) => {
            const isLiked = review.likedBy?.includes(user?.uid);
            if (isLiked) {
              unlikeReview(reviewId);
            } else {
              likeReview(reviewId);
            }
          }}
          onDislike={() => {}}
          onReport={() => {}}
          onHistory={() => {}}
          showUserReviewsOnly={false}
        />
      ))}
    </Box>
  );
}

function FavoriteRestaurantsSection({
  restaurants,
  loading,
  likeRestaurant,
  unlikeRestaurant,
}) {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[...Array(8)].map((_, index) => (
          <Grid size={{ xs: 12, sm: 4 }} key={index}>
            <Card
              sx={{
                maxWidth: 345,
                height: "fit-content",
                display: "flex",
                flexDirection: "column",
                borderRadius: 4,
              }}
            >
              <CardHeader
                avatar={<Skeleton variant="circular" width={40} height={40} />}
                title={<Skeleton variant="text" width="60%" />}
              />
              <CardContent sx={{ paddingTop: 0 }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="90%" />
              </CardContent>
              <CardActions sx={{ mt: "auto" }}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={32}
                  sx={{ borderRadius: 2 }}
                />
                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (restaurants.length === 0) {
    return (
      <NotificationTypography
        color="info"
        Icon={SearchOffIcon}
        title="No Favorite Restaurants"
        message="You haven't favorited any restaurants yet."
        showActions={false}
      />
    );
  }

  return (
    <RestaurantGrid
      restaurants={restaurants}
      likeRestaurant={likeRestaurant}
      unlikeRestaurant={unlikeRestaurant}
    />
  );
}

function RestaurantGrid({ restaurants, likeRestaurant, unlikeRestaurant }) {
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { user } = useAuthentication();

  const handleLikeClick = useCallback(
    async (event, restaurant) => {
      event.preventDefault();
      event.stopPropagation();

      if (!user) {
        const result = await confirm({
          title: "Sign in required",
          description: `Sign in to add to favourites?`,
          confirmLabel: "Sign in",
          cancelLabel: "Cancel",
        });

        if (result) {
          window.location.href = API_ENDPOINTS.AUTH.SIGN_IN;
        }

        return;
      }

      const liked = !!(user && restaurant.likedBy?.includes(user.uid));

      if (liked) {
        await unlikeRestaurant(restaurant.id);
      } else {
        await likeRestaurant(restaurant.id);
      }
    },
    [user, confirm, likeRestaurant, unlikeRestaurant],
  );

  return (
    <Grid container spacing={2}>
      {restaurants.map((restaurant) => {
        const liked = !!(user && restaurant.likedBy?.includes(user.uid));

        return (
          <Grid size={{ xs: 12, sm: 4 }} key={restaurant.id}>
            <Card
              sx={{
                maxWidth: 345,
                height: "fit-content",
                display: "flex",
                flexDirection: "column",
                transition:
                  "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: 6,
                },
                borderRadius: 4,
              }}
              onClick={() => {
                navigate(`/restaurant/${restaurant.id}`, {
                  replace: false,
                  state: { scrollToTop: true },
                });
              }}
              style={{ cursor: "pointer" }}
            >
              <CardHeader
                title={restaurant.name}
                avatar={
                  <Avatar
                    aria-label="restaurant"
                    src={
                      restaurant.logoUrl && restaurant.logoUrl.trim() !== ""
                        ? restaurant.logoUrl
                        : undefined
                    }
                  >
                    {(!restaurant.logoUrl || restaurant.logoUrl.trim() === "") &&
                      restaurant.name?.charAt(0).toUpperCase()}
                  </Avatar>
                }
              />
              <CardContent
                sx={{
                  paddingTop: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {restaurant.phoneNumber && (
                  <Typography
                    component="a"
                    href={`tel:${restaurant.phoneNumber}`}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      mb: 0.5,
                      textDecoration: "none",
                      "&:hover": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <CallIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: "middle" }}
                    />
                    {restaurant.phoneNumber}
                  </Typography>
                )}
                {restaurant.websiteUrl && (
                  <Typography
                    component="a"
                    href={
                      restaurant.websiteUrl.startsWith("http")
                        ? restaurant.websiteUrl
                        : `https://${restaurant.websiteUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      mb: 0.5,
                      textDecoration: "none",
                      "&:hover": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <PublicIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: "middle" }}
                    />
                    {restaurant.websiteUrl}
                  </Typography>
                )}

                {restaurant.status !== "disabled" ? (
                  <OpenHoursTypography restaurant={restaurant} />
                ) : (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <BlockIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: "middle" }}
                    />
                    <Box
                      component="span"
                      sx={{ color: "error.main", fontWeight: 500, mr: 0.5 }}
                    >
                      This restaurant is disabled
                    </Box>
                  </Typography>
                )}

                {/* AUD-17: mainLocation is now on the root document —
                  no subcollection read needed. Wrap in array for ChipButton. */}
                {restaurant.mainLocation && (
                  <ChipButton locations={[restaurant.mainLocation]} />
                )}
              </CardContent>
              <CardActions sx={{ mt: "auto", pt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <RatingChip
                    rating={restaurant.rating}
                    count={restaurant.reviewCount}
                  />
                  <Box sx={{ display: "flex" }}>
                    <LikeChip
                      count={restaurant.likedBy?.length || 0}
                      liked={liked}
                      onClick={(e) => handleLikeClick(e, restaurant)}
                    />
                    <ShareButton restaurantId={restaurant.id} />
                  </Box>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
